/**
 * LoginScreen — admin sign-in page.
 *
 * Sitra Entra ID SSO for Sitra staff; an email code for everyone else with an
 * account, such as a dataset's outside contributor (#799), who lands back on
 * return_to. Frontend page lives here on Vercel; the OIDC dance happens on the
 * backend (apps/backend/src/routes/auth.entra.js), the code in auth.otp.js.
 *
 * Route: /login
 * Query params:
 *   - return_to: where to land after sign-in (default /admin). Must be a path.
 */
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../config/api';
import { useTranslation } from '../hooks/useTranslation.jsx';
import EmailOtpForm from '../components/auth/EmailOtpForm';

const Page = styled.div`
  min-height: 100vh;
  background: #000;
  color: #fff;
  font-family: 'Rational', 'Inter', -apple-system, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: linear-gradient(180deg, #000 0%, #111 100%);
  border: 1px solid #222;
  width: 100%;
  max-width: 420px;
  padding: 48px 40px;
  position: relative;

  /* Sitra yellow accent stripe */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 6px;
    background: #FFD93D;
  }
`;

const Brand = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 24px;
  display: flex;
  gap: 8px;
  .sitra { color: #FFD93D; }
  .sep   { color: rgba(255, 255, 255, 0.3); }
  .kuutio { color: #fff; }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 32px;
  line-height: 1.5;
`;

const SsoButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px 20px;
  background: #fff;
  color: #000;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border: 2px solid #fff;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #FFD93D;
    border-color: #FFD93D;
  }

  /* Microsoft logo (4 squares) */
  .ms-logo {
    width: 18px;
    height: 18px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 1px;
    flex-shrink: 0;
  }
  .ms-logo span:nth-of-type(1) { background: #f25022; }
  .ms-logo span:nth-of-type(2) { background: #7fba00; }
  .ms-logo span:nth-of-type(3) { background: #00a4ef; }
  .ms-logo span:nth-of-type(4) { background: #ffb900; }
`;

const Or = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 24px 0 12px;
  text-align: center;
`;

const Hint = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 24px 0 0;
  text-align: center;
  line-height: 1.5;
`;

export default function LoginScreen() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || '/admin';

  // Path validation — only allow internal paths so a malicious link can't redirect off-site
  const safeReturn = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/admin';
  // return_to_origin lets the backend bounce us back to whichever frontend origin
  // we started from (allowlisted server-side). Critical for localhost↔dev-backend.
  const ssoUrl = `${API_ENDPOINTS.AUTH_ENTRA_LOGIN()}?return_to=${encodeURIComponent(safeReturn)}&return_to_origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <Page>
      <Card>
        <Brand>
          <span className="sitra">SITRA</span>
          <span className="sep">/</span>
          <span className="kuutio">KUUTIO</span>
        </Brand>

        <Title>{t('login.title')}</Title>
        <Subtitle>{t('login.subtitle')}</Subtitle>

        <SsoButton href={ssoUrl}>
          <span className="ms-logo" aria-hidden="true">
            <span></span><span></span><span></span><span></span>
          </span>
          {t('login.microsoft')}
        </SsoButton>

        <Or>{t('login.orEmail')}</Or>
        <EmailOtpForm redirectTo={safeReturn} note={t('login.emailNote')} />

        <Hint>{t('login.hint')}</Hint>
      </Card>
    </Page>
  );
}
